import { z } from "zod";
import {
  flowAssignmentDtoSchema,
  flowAssignmentSourceSchema,
  flowTimelineItemSchema,
  flowTriggerSchema,
} from "./flows";

// TES-224 journey layer — a Journey is a versioned, admin-authored grouping of
// Flows. Each member Flow carries `journeyKey` pointing back here; a patient is
// enrolled via a JourneyAssignment and member flows activate per their own
// trigger. Authoring mirrors the Flow draft/published/versioned style; copy is
// carried on the member flows, so a Journey is just header + an ordered list of
// member flow keys. No PII — template definition only.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const JOURNEY_STATUSES = ["draft", "published", "archived"] as const;
export const journeyStatusSchema = z.enum(JOURNEY_STATUSES);
export type JourneyStatus = z.infer<typeof journeyStatusSchema>;

export const JOURNEY_ASSIGNMENT_STATUSES = [
  "active",
  "completed",
  "cancelled",
] as const;
export const journeyAssignmentStatusSchema = z.enum(
  JOURNEY_ASSIGNMENT_STATUSES,
);
export type JourneyAssignmentStatus = z.infer<
  typeof journeyAssignmentStatusSchema
>;

// ---------------------------------------------------------------------------
// Journey template (draft/published, versioned)
// ---------------------------------------------------------------------------

// The editable journey definition — header + the ordered set of member flow
// keys. The flows themselves are authored separately (each with its own
// trigger + items); the journey just groups + orders them.
export const journeyContentSchema = z.object({
  key: z.string().min(1),
  version: z.number().int().positive(),
  name: z.string().min(1),
  description: z.string().optional(),
  memberFlowKeys: z.array(z.string().min(1)),
  // How a patient gets enrolled. 'manual' (clinician attaches) or 'event'
  // (onEvent enrolls when trigger.event fires) — same shape as a flow's
  // trigger, reusing the typed FLOW_EVENTS catalog.
  trigger: flowTriggerSchema,
});
export type JourneyContent = z.infer<typeof journeyContentSchema>;

// The admin editor view: live published version + the single in-flight draft.
export const journeyEditorViewSchema = z.object({
  published: journeyContentSchema.nullable(),
  draft: journeyContentSchema.nullable(),
});
export type JourneyEditorView = z.infer<typeof journeyEditorViewSchema>;

// Row in the admin journey list (GET /journeys).
export const journeySummarySchema = z.object({
  key: z.string(),
  name: z.string(),
  status: journeyStatusSchema,
  version: z.number().int(),
  memberCount: z.number().int(),
  trigger: flowTriggerSchema,
  updatedAt: z.string(),
});
export type JourneySummary = z.infer<typeof journeySummarySchema>;

// Body to create/update a draft journey. `trigger` optional — omitted keeps
// the pre-existing manual behavior (and old clients keep working).
export const upsertJourneyDraftSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  memberFlowKeys: z.array(z.string().min(1)),
  trigger: flowTriggerSchema.optional(),
});
export type UpsertJourneyDraft = z.infer<typeof upsertJourneyDraftSchema>;

// Body to create a brand-new journey — the draft fields plus the stable lookup
// `key` (lowercase slug, unique per tenant).
export const createJourneyRequestSchema = upsertJourneyDraftSchema.extend({
  key: z
    .string()
    .min(1)
    .regex(/^[a-z0-9_-]+$/, "key must be a lowercase slug (a-z, 0-9, -, _)"),
});
export type CreateJourneyRequest = z.infer<typeof createJourneyRequestSchema>;

// ---------------------------------------------------------------------------
// Assignment + calendar DTOs
// ---------------------------------------------------------------------------

// Body to attach a journey to a patient. The admin attaches by `journeyKey`
// (the server resolves the latest published version).
export const attachJourneyRequestSchema = z.object({
  journeyKey: z.string().min(1),
  // ISO datetime; defaults to now server-side (the day-0 anchor for member
  // flows assigned at attach time).
  startedAt: z.string().datetime().optional(),
});
export type AttachJourneyRequest = z.infer<typeof attachJourneyRequestSchema>;

export const journeyAssignmentDtoSchema = z.object({
  id: z.string(),
  journeyKey: z.string(),
  journeyName: z.string(),
  journeyVersion: z.number().int(),
  source: flowAssignmentSourceSchema,
  startedAt: z.string(),
  status: journeyAssignmentStatusSchema,
});
export type JourneyAssignmentDto = z.infer<typeof journeyAssignmentDtoSchema>;

// One materialized item in the per-patient calendar, carrying its journey +
// flow provenance so the admin can group/colour it. `journeyKey`/`journeyName`
// are null for a standalone flow's item.
export const journeyCalendarEntrySchema = flowTimelineItemSchema.extend({
  journeyKey: z.string().nullable(),
  journeyName: z.string().nullable(),
  flowKey: z.string(),
  flowName: z.string(),
});
export type JourneyCalendarEntry = z.infer<typeof journeyCalendarEntrySchema>;

// Entries grouped by their concrete due date.
export const journeyCalendarDaySchema = z.object({
  dueOn: z.string(),
  entries: z.array(journeyCalendarEntrySchema),
});
export type JourneyCalendarDay = z.infer<typeof journeyCalendarDaySchema>;

// GET /employee-api/v1/patients/:id/calendar — the per-patient calendar over a
// window (default 30 days): active journeys, any standalone flow assignments,
// and the day-grouped materialized items with provenance.
export const flowPatientCalendarSchema = z.object({
  journeys: z.array(journeyAssignmentDtoSchema),
  standaloneAssignments: z.array(flowAssignmentDtoSchema),
  calendar: z.array(journeyCalendarDaySchema),
});
export type FlowPatientCalendar = z.infer<typeof flowPatientCalendarSchema>;
